/* eslint-disable guard-for-in */
/* eslint-disable max-depth */
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import ROLES, { rolesArray } from '../utils/roles';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useHiringCandidateLanding } from './HiringCandidateRedirect';
import AppLoader from '../components/AppLoader';
import ErrorPage from '../components/ErrorPage';
import EditProfileForm from '../pages/new-designs/components/EditProfileForm';
import HobbiesForm from '../pages/new-designs/components/HobbiesForm';
import SocialHandlesForm from '../pages/new-designs/components/SocialHandlesForm';
import DisabilityForm from '../pages/new-designs/components/DisabilityForm';
import DigitalIntroForm from '../pages/new-designs/components/DigitalIntroForm';
import EducationForm from '../pages/new-designs/components/EducationForm';
import WorkHistoryForm from '../pages/new-designs/components/WorkHistoryForm';
import useProfileDetails from '../hooks/useProfileDetails';
import OnboardingQuestions from '../pages/new-designs/components/OnboardingQuestions';
import { AGILE_ORG, RDP_CREATIVE } from '../utils/constants';
import useResponsiveScreenSwitch from '../utils/useResponsiveScreenSwitch';
import OnboardingProcessNew from '../pages/new-designs/tabs/profile/OnboardingProcessNew';
import useOrgSettings from '../hooks/useOrgSettings';
import OnboardingProcessIndustry from '../pages/new-designs/tabs/profile/OnboardingProcessIndustry';
import { popupCountActions } from '../redux/slices/poupsCountsSlice';
import Company from '../pages/new-designs/components/Company';
import CompanyPosition from '../pages/new-designs/components/CompanyPosition';
import CompanyInformation from '../pages/new-designs/components/CompanyInformation';
import LearningObjectives from '../pages/new-designs/tabs/profile/OnboardingLearningObjectives';
import OnboardingDepartment from '../pages/new-designs/tabs/profile/OnboardingDepartment';
import useProgramFieldUnion from '../hooks/useProgramFieldUnion';
import { useMemo, useRef } from 'react';
import OnboardingDialog from '../pages/new-designs/onboarding/OnboardingDialog';
import OnboardingShell from '../pages/new-designs/onboarding/OnboardingShell';
import { useMemberWord } from '../utils/memberTypeLabels';

const ASSESSMENT_PAGE = 'Assessment';

const Redirect = () => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const count = useAppSelector((state) => state.popupsCount.count);
  const { loading, data } = useAppSelector((state) => state.mentorTasks);
  const user = useAppSelector((state) => state.userProfile);
  let role = localStorage.getItem('role') || '';
  const isAuth = localStorage.getItem('isAuthenticated');
  const isLoggedIn = isAuth ? JSON.parse(isAuth) : false;
  const { ftue, mentee, mentors, orgRoles } = useProfileDetails();
  // A Hiring candidate goes to their assessment, not through the member
  // first-time setup this file opens below (DA-5042 C-9).
  const candidateLanding = useHiringCandidateLanding();
  const { settings, firstName, category, lastseen } = useProfileDetails() || {
    settings: { disabilityType: undefined }
  };

  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const isMentor = lastseen?.role === 'mentor';
  const isMentee = lastseen?.role === 'mentee';
  // console.log('settings', settings);
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const w = useMemberWord();
  const { forMobile } = useResponsiveScreenSwitch();
  const dispatch = useDispatch();

  // Merge BE-provided settings with the union of optional fields enabled across
  // ALL of the user's programs. The BE only derives `settings` from a single
  // group (lastSeen or first), so if another program requires an extra field
  // (e.g. department) we'd otherwise skip its screen during onboarding and
  // surface it later in the post-FTUE MissingMandatoryFields modal — feeling
  // inconsistent to the user.
  const { union: programFieldUnion, ready: programFieldsReady } = useProgramFieldUnion();
  // Org details still on their first fetch: orgSettings drives the step count.
  const orgDetailsPending = useAppSelector((state) => state.orgDetails.loading && !state.orgDetails.data);
  // True while the wizard is on screen (see the loader gate in checkUserProfile).
  const wizardShownRef = useRef(false);
  const effectiveSettings = useMemo(
    () => ({ ...(settings ?? {}), ...programFieldUnion }),
    [settings, programFieldUnion]
  );
  const hasProgramFields = Object.keys(programFieldUnion).length > 0;

  interface Popup {
    component: React.ReactNode;
    name: string;
    heading: string;
    subHeading?: string;
  }

  const isOrgSettings = useOrgSettings();
  /** Profile Information */
  const newPopups: Popup[] = [
    {
      component: <EditProfileForm />,
      name: 'Profile Information',
      heading: "Let's set up your profile",
      subHeading: "This is how you'll appear to others in your program"
    }
  ];

  /** Department (multi-select) — placed early so it's reached even if a later
   *  form (e.g. WorkHistoryForm) has hardcoded "next step" logic that prematurely
   *  ends FTUE. */
  if (effectiveSettings?.department !== undefined) {
    newPopups.push({
      component: <OnboardingDepartment />,
      name: 'Department',
      heading: 'Which team are you on?',
      subHeading: 'Pick any that apply'
    });
  }

  if (effectiveSettings?.videoIntro !== undefined) {
    newPopups.push({
      component: <DigitalIntroForm />,
      name: 'Video Intro',
      heading: 'Say hello in 60 seconds',
      subHeading:
        effectiveSettings?.videoIntro === true
          ? 'A short clip helps your matches get to know you'
          : 'A short clip gets more replies. You can skip it.'
    });
  }

  /** Basic setting to selected field — also open the block when at least one
   *  of the user's programs has a field configured, even if the org-level
   *  orgSettings flag hasn't been flipped yet. */
  if (isOrgSettings || hasProgramFields) {
    /** Company Form */
    if (effectiveSettings?.companyName !== undefined) {
      newPopups.push({
        component: <Company />,
        name: 'Company',
        heading: 'Tell us about your company',
        subHeading: "The company you're joining the program with"
      });
    }

    /** Company Information */
    if (effectiveSettings?.companyInfo !== undefined) {
      newPopups.push({
        component: <CompanyInformation />,
        name: 'Company Information',
        heading: 'A little more about your company',
        subHeading: `Your ${w('mentor', { plural: true, lower: true })} use this to give sharper advice`
      });
    }

    /** Company Position Form */
    if (effectiveSettings?.companyPosition !== undefined) {
    newPopups.push({
      component: <CompanyPosition />,
      name: 'Company Position',
      heading: "What's your role there?",
      subHeading: 'Your position at the company'
    });
  }

    /** Learning Objectives */
    if (effectiveSettings?.learningObjectives !== undefined && (isMentor || isMentee)) {
      newPopups.push({
        component: <LearningObjectives />,
        name: `${isMentor ? 'Areas of Expertise' : 'Learning Objectives'}`,
        heading: isMentor ? 'Where can you help?' : 'What do you want to get out of this?',
        subHeading: isMentor
          ? 'Select the areas in which you can help people in this program'
          : 'Pick the objectives your program will focus on'
      });
    }

    /** Social handles */
    if (effectiveSettings?.socials !== undefined) {
      newPopups.push({
        component: <SocialHandlesForm />,
        name: 'Social Handles',
        heading: 'Where else can people find you?',
        subHeading:
          effectiveSettings?.socials === true
            ? "Add the profiles you'd like to share"
            : "Optional, add any you'd like to share"
      });
    }

    /** Hobbies Form */
    if (oid !== AGILE_ORG && effectiveSettings?.hobbiesInterests !== undefined) {
      newPopups.push({
        component: <HobbiesForm />,
        name: 'Hobbies and interests',
        heading: 'What do you do for fun?',
        subHeading: 'Helps break the ice with your matches'
      });
    }

    /** Topics & Industry */
    //  if ((mentee.length > 0 || mentors.length > 0) && oid !== RDP_CREATIVE && settings && settings.topics!== undefined ) {
    //   newPopups.push({
    //     component: <OnboardingProcessNew />,
    //      name: 'Select the topics most relevant to you',
    //      heading: 'Select the topics most relevant to you'
    //    });
    //   }
    //   if ((mentee.length > 0 || mentors.length > 0) && oid !== RDP_CREATIVE  && settings && settings.industries!== undefined ){
    //    newPopups.push({
    //     component: <OnboardingProcessIndustry />,
    //     name: 'Select the industries most relevant to you',
    //      heading: 'Select the industries most relevant to you'
    //    });
    //  }

    if (
      (mentee.length > 0 || mentors.length > 0) &&
      oid !== RDP_CREATIVE &&
      effectiveSettings?.subjects !== undefined
    ) {
      newPopups.push({
        component: <OnboardingProcessNew />,
        name: 'Select the topics most relevant to you',
        heading: 'Pick a few areas that interest you',
        subHeading: 'Tap all that apply, this shapes your matches'
      });
      newPopups.push({
        component: <OnboardingProcessIndustry />,
        name: 'Select the industries most relevant to you',
        heading: 'Which industries are relevant to you?',
        subHeading: 'Tap all that apply'
      });
    }

    /** Education Form */
    if (oid !== AGILE_ORG && effectiveSettings?.educationHistory !== undefined) {
      newPopups.push({
        component: <EducationForm />,
        name: 'Education History',
        heading: 'And your education',
        subHeading:
          effectiveSettings?.educationHistory === true
            ? 'Add your most relevant education'
            : "Add anything relevant, or skip if it's not"
      });
    }

    /** Work History Form */
    if (effectiveSettings?.workHistory !== undefined) {
      newPopups.push({
        component: <WorkHistoryForm />,
        name: 'Work History',
        heading: 'Add your work history',
        subHeading: 'A quick sketch helps us match you well'
      });
    }

    /** Member Attributes — now handled inline in EditProfileForm (Step 1) */

    /** Disability Form */
    if (effectiveSettings?.disabilityType !== undefined) {
      newPopups.push({
        component: <DisabilityForm />,
        name: 'Disability',
        heading: 'Accessibility needs',
        subHeading: "Used only for your org's inclusion reporting, and you can always select prefer not to say"
      });
    }

    /** Onboarding Questions */
    if (data?.data?.length > 0) {
      newPopups.push({
        component: <OnboardingQuestions />,
        name: ASSESSMENT_PAGE,
        // One question per screen: the assessment page renders its own heading.
        heading: ''
      });
    }
  }

  // Admin-ONLY invitees onboard with the profile step only. The remaining
  // popups (departments, objectives, work history, assessment) are
  // member-onboarding screens that render empty for them, wedging the wizard
  // on a blank dialog with ftue never flipped. An admin who is also a learner
  // (invited to the org as mentee/mentor, or enrolled as one in a program)
  // gets the full learner onboarding instead (product decision 2026-09-10).
  const isLearner =
    mentee.length > 0 ||
    mentors.length > 0 ||
    orgRoles.includes('mentee') ||
    orgRoles.includes('mentor');
  const isAdminOnboarding =
    (lastseen?.role === 'admin' || role === ROLES.orgAdmin) && !isLearner;
  if (isAdminOnboarding) newPopups.length = 1;

  const totalSteps = newPopups.length;
  dispatch(popupCountActions.setPopupLength(newPopups.length));
  // Each assessment question is its own step: the assessment page counts once
  // in newPopups, then one more per extra question.
  const hasAssessmentPage = newPopups.some((p) => p.name === ASSESSMENT_PAGE);
  const questionCount = hasAssessmentPage && data?.data?.length > 0 ? data.data.length - 1 : 0;
  const totalCount = Math.max(1, totalSteps + questionCount);
  const showPopups = () => {
    const stepNumber = Math.min(count + 1, totalCount);
    const page = newPopups[count];
    // Past the last page the wizard is inside the assessment, which renders one
    // question per screen and owns its heading.
    const inAssessment = count >= newPopups.length;
    return (
      <OnboardingDialog
        open={ftue}
        onClose={() => {}}
        aria-labelledby="onboardingProcess-dialog-title"
        aria-describedby="onboardingProcess-dialog-description"
        data-testid="onboarding-dialog"
        disableEnforceFocus
        fullScreen={forMobile}
        // No fade-in: the loader hands straight to the first page.
        transitionDuration={0}
      >
        <OnboardingShell
          progress={(stepNumber / totalCount) * 100}
          stepLabel={`Step ${stepNumber} of ${totalCount}`}
          heading={inAssessment ? undefined : page?.heading}
          hint={inAssessment ? undefined : page?.subHeading}
        />
        {inAssessment ? (hasAssessmentPage && !isAdminOnboarding ? <OnboardingQuestions /> : null) : page?.component}
      </OnboardingDialog>
    );
  };

  /** Check user type */
  const checkUserProfile = () => {
    // Phones used to stop here on a "please activate on a laptop or desktop" page.
    // The app now has a phone shell (src/mobile), so a phone lands in the app too.
    if (role && rolesArray.includes(role)) {
      // to get the no of steps we need the response, that's why loader added for tasks.
      // Only until the wizard first shows: once it is on screen, a refetch of the profile
      // or of the tasks (the dashboard behind it fetches them) must not swap it for the
      // loader, or the current step remounts and wipes what the member already picked.
      const wizardOnScreen = wizardShownRef.current && ftue && !!user.data;
      if (!wizardOnScreen && (user.loading || (ftue && loading))) {
        wizardShownRef.current = false;
        return <AppLoader />;
      }
      // Hold the wizard's FIRST appearance until everything its page list depends
      // on has settled: the program-field union (adds pages) and org details
      // (orgSettings turns "Step 1 of 1" into the real count). Rendering earlier
      // flashes a stray page and jumps the progress bar. Once the wizard is on
      // screen, a later refetch must not swap it for the loader: that remounts
      // the current step and wipes its input.
      if (ftue && !wizardShownRef.current && (!programFieldsReady || orgDetailsPending)) return <AppLoader />;
      if (user.error) return <ErrorPage message={user.errorText} />;
      if (user.data) {
        if (role !== ROLES.platAdmin && ftue) {
          // console.log('ftue', ftue);
          if (role === ROLES.orgAdmin && !isOrgSettings && firstName !== '') {
            return <Navigate to="/redirect" />;
          }
          wizardShownRef.current = true;
          return showPopups();
        } else {
          return <Navigate to="/redirect" />;
        }
      }
      // Signed in but the profile fetch has not started yet (basic settings are
      // still loading): keep the loader up instead of flashing a blank page.
      return <AppLoader />;
    }
    if (role && !rolesArray.includes(role)) {
      return <ErrorPage message="This person does not have a role associated" />;
    }
    // if (user.data) return <h1>Done</h1>;
    // return <ErrorPage message="Something went wrong, please try again" />;
    // Signed in but the role is not stored yet (the token is still being read).
    return <AppLoader />;
  };

  /** Loader */
  if (isLoading) return <AppLoader />;
  // if (error) return <ErrorPage message={error.message}/>;

  if (error) {
    if (error.message === 'the specified account is not allowed to accept the current invitation') {
      return <ErrorPage message="You can only activate your account with the email address used on your invitation" />;
    } else {
      return <ErrorPage message={error.message} />;
    }
  }

  const mobileAppLinkPage = localStorage.getItem('mobileAppLinkPage');
  const invitation = localStorage.getItem('invitation');

  // if (mobileAppLinkPage !== 'true' && isMobile() && (!invitation || (isAuth && ftue))) {
  //   return <Navigate to="/download-app" />;
  // }
  if (isAuthenticated || isLoggedIn) {
    // Before anything else: the member first-time wizard below asks for
    // attributes and a member type, which a candidate may not see and which
    // would make them match audiences like a member (C-9, PRD 6.2). Their
    // onboarding is the round's own.
    if (candidateLanding === 'asking') return <AppLoader />;
    if (candidateLanding === 'candidate') return <Navigate to="/hiring" replace={true} />;
    return checkUserProfile();
  }
  return <Navigate to="/login" replace={true} />;
};

export default Redirect;
